export function originationCapPercent(general) {
  const cap = Number(general?.originationCapPercent);
  return Number.isFinite(cap) && cap >= 1 && cap <= 100 ? cap : 75;
}

/**
 * The cap that applies to a row. The firm maximum, always — including a one-row split.
 *
 * This used to exempt a lone originator, on the reasoning that the total must be 100% and a
 * sub-100 cap would leave a one-row split unsatisfiable. That was the wrong way round: the firm
 * rule is that NO attorney holds more than the cap, so a matter one attorney would otherwise
 * take all the credit for is exactly the case the cap exists for. The consequence is deliberate
 * and is stated in the message rather than engineered around — reaching 100% takes a second
 * name on the split.
 *
 * `rowCount` is kept in the signature because the call sites read better for it and because a
 * future rule may well depend on it again; it is ignored on purpose.
 */
export function effectiveOriginationCap(capPercent /* , rowCount */) {
  return capPercent;
}

export function clampOriginationPercent(value, capPercent) {
  if (value === '') return '';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 0;
  return Math.max(0, Math.min(numeric, capPercent));
}

/**
 * Every way an origination split can be wrong, in one place.
 *
 * This is the single source of truth for the rule, used by three callers that must not drift:
 * the control renders these as a red notice the moment they appear, `fieldRules.js` turns them
 * into blocking problems that gate submission and page navigation, and the server re-checks the
 * same conditions at submit. Three implementations of "does this split add up" would eventually
 * disagree, and the one that disagreed quietly would be the one that mattered.
 *
 * ALL of these are hard errors. There is no soft "are you sure" any more: a 0% row used to be a
 * prompt you could answer with a reason, on the reasoning that an attorney might work a matter
 * without originating it. The firm's answer is that such a person belongs on the Working
 * Attorneys list and not in the split at all, which is both simpler and unambiguous to Finance.
 *
 * Ordered so the submitter is told one fixable thing at a time: rows that name nobody first,
 * then duplicates, then individual percentages, then the total. A percentage complaint about a
 * row with no attorney on it, or a total computed over junk rows, is noise.
 *
 * @param {Array} rows       the field's value
 * @param {Object} options
 * @param {number} options.capPercent  the firm's per-attorney maximum for a SHARED split
 * @param {(uid: string) => string} [options.nameOf]  resolves a uid to a person's name
 * @returns {Array<{code: string, message: string}>}
 */
export function originationProblems(rows, { capPercent = 75, nameOf } = {}) {
  const list = Array.isArray(rows) ? rows : [];
  if (!list.length) return [];
  const problems = [];
  const name = (uid) => (nameOf ? nameOf(uid) : uid) || uid;
  const nameList = (uids) => {
    const names = uids.map(name);
    if (names.length <= 1) return names.join('');
    return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  };

  const named = list.filter((row) => row && row.uid);
  const unnamed = list.length - named.length;
  if (unnamed > 0) {
    problems.push({
      code: 'unnamed',
      message:
        unnamed === 1
          ? 'One row has no attorney selected — choose one, or remove the row.'
          : `${unnamed} rows have no attorney selected — choose one for each, or remove them.`,
    });
    // Everything below is about percentages, which cannot be judged while a row names nobody.
    return problems;
  }

  const seen = new Set();
  const duplicated = named.filter((row) => (seen.has(row.uid) ? true : (seen.add(row.uid), false)));
  if (duplicated.length) {
    problems.push({
      code: 'duplicate',
      message: `The same attorney appears more than once (${duplicated.length} duplicate ${
        duplicated.length === 1 ? 'row' : 'rows'
      }) — give each attorney a single row.`,
    });
    return problems;
  }

  // Per-row percentages, before the total: a total is only meaningful once each share is legal.
  const zero = named.filter((row) => !(Number(row.percent) > 0));
  if (zero.length) {
    problems.push({
      code: 'zero',
      message: `${nameList(zero.map((row) => row.uid))} ${
        zero.length === 1 ? 'has' : 'have'
      } no origination share. Every attorney in the split must have more than 0% — remove ${
        zero.length === 1 ? 'them' : 'them'
      } from the split if they are only a working attorney.`,
    });
  }

  const overWhole = named.filter((row) => Number(row.percent) > 100);
  if (overWhole.length) {
    problems.push({
      code: 'over_100',
      message: `${nameList(overWhole.map((row) => row.uid))} ${
        overWhole.length === 1 ? 'is' : 'are'
      } over 100%. No attorney can hold more than the whole matter.`,
    });
  } else {
    // Only worth raising once nobody is over 100% — otherwise the same row draws two complaints.
    const cap = effectiveOriginationCap(capPercent, named.length);
    const overCap = named.filter((row) => Number(row.percent) > cap);
    if (overCap.length) {
      problems.push({
        code: 'over_cap',
        // With one name on the split, "redistribute" is not advice anyone can act on — there is
        // nothing to redistribute between. Say the actual next step instead.
        message: `${nameList(overCap.map((row) => row.uid))} ${
          overCap.length === 1 ? 'exceeds' : 'exceed'
        } the firm maximum of ${cap}% per attorney — ${
          named.length === 1
            ? `add a second attorney to carry the remaining ${100 - cap}%.`
            : 'redistribute the split.'
        }`,
      });
    }
  }

  const total = named.reduce((sum, row) => sum + (Number(row.percent) || 0), 0);
  const rounded = Math.round(total);
  if (rounded > 100) {
    problems.push({
      code: 'total_over',
      message: `The split totals ${total}%. It cannot come to more than 100%.`,
    });
  } else if (rounded < 100) {
    problems.push({
      code: 'total_under',
      message: `The split totals ${total}%. It has to come to exactly 100%.`,
    });
  }

  return problems;
}
